import React, { useEffect, useRef, useState } from "react"
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import CloseIcon from "../../images/CloseIcon";
import { deleteOrderTemplates, getListOrderTemplates, updateOrderTemplates } from "../../services/ordertemplates.service";
import PopupLocation from "../../components/PopupLocation";
import PlusIconWhite from "../../images/PlusIconWhite";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import { useResponsive } from "../../hook/useResponsive";
import OrderTemplatesIcon from "../../images/OrderTemplatesIcon";
import { showDeleteToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import {
    ListColumn,
    ListQuery,
    ListRowAction,
    ListScreen,
    ListScreenHandle,
    cells,
} from '../../components/list';

interface OrderTemplateRow {
    id: string;
    templateName: string;
    description: string;
    validFrom: Date;
    validTo: Date;
    location: { name: string } | null;
    updatedAt: Date;
    isActive: boolean;
}
interface OrderTemplatesResponse {
    data: {
        data: {
            orderTemplates: OrderTemplateRow[];
            count: number
        }
    }

}
const OrderTemplates = () => {
    const preLanguage = localStorage.getItem("language");
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<ListScreenHandle>(null);
    const [language, setLanguage] = useState<any>(null);
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [idDelete, setIdDelete] = useState<string>("")
    /** Status flips made on this page since the last fetch (the list only ever loads active templates). */
    const [statusOverrides, setStatusOverrides] = useState<Record<string, boolean>>({});
    const inforTable: any = {
        img: <OrderTemplatesIcon />,
        title: language && language?.["No order templates yet"] ? language?.["No order templates yet"] : "No order templates yet", path: "/orderTemplates/new", desc: language && language?.["Create a template to save your order configurations for faster, error-free orders."] ? language?.["Create a template to save your order configurations for faster, error-free orders."] : "Create a template to save your order configurations for faster, error-free orders.", titleCreate: language && language?.["New Order Templates"] ? language?.["New Order Templates"] : "New Order Templates"
    };
    const { isMobile } = useResponsive();

    const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            event.preventDefault();
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };

    }, []);
    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);
    const formatDate = (dateString: any) => {
        const date = new Date(dateString);
        if (!dateString || Number.isNaN(date.getTime())) return '';
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }
    /** One page of templates for the selected location; the search box maps onto `searchValue`. */
    const fetchOrderTemplates = async (query: ListQuery) => {
        const locationId = localStorage.getItem("locationId") || "";
        if (locationId === "") {
            setShowPopup(true)
            return { rows: [], count: 0 };
        }
        webLoadingFalse();
        const response: OrderTemplatesResponse = await getListOrderTemplates(query.page, query.limit, locationId, query.search, true);
        setStatusOverrides({});
        return {
            rows: response.data.data.orderTemplates ?? [],
            count: response.data.data.count ?? 0,
        };
    };

    const goToTemplate = (id: string) => webNavigate('/' + preLanguage + '/orderTemplates/' + id);
    const handleShowDelete = (id: string) => {
        setShowDelete(true);
        setIdDelete(id)
    }
    const handleDeleteOrderTemplate = async () => {
        try {
            const response = await deleteOrderTemplates(idDelete);
            showDeleteToast(language?.['Order Template Deleted !'] ?? 'Order Template Deleted !');
        } catch (error) {
            showApiError(error, { language });
        } finally {
            listRef.current?.reload();
            setShowDelete(false)
        }
    }

    const isActiveOf = (row: OrderTemplateRow) => statusOverrides[row.id] ?? !!row.isActive;

    const handleUpdateStatus = async (row: OrderTemplateRow) => {
        const nextActive = !isActiveOf(row);
        setStatusOverrides(prev => ({ ...prev, [row.id]: nextActive }));
        try {
            // The API row carries every template field; send it back whole with the flag flipped (as before).
            const payload: any = { ...row, isActive: nextActive }
            const response = await updateOrderTemplates(row.id, payload);
        } catch (error) {
            setStatusOverrides(prev => ({ ...prev, [row.id]: !nextActive }));
            showApiError(error, { language });
        } finally {
        }
    }

    /* ---------------- List kit configuration ---------------- */

    /** The same on/off switch the shared table drew; stops the tap so the mobile card does not open the record sheet. */
    const statusToggle = (row: OrderTemplateRow) => {
        const on = isActiveOf(row);
        return (
            <div className="flex items-center gap-x-3">
                <div
                    className={`switch ${on ? 'on' : 'off'}`}
                    onClick={(e) => { e.stopPropagation(); handleUpdateStatus(row); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                >
                    <div className={`shadow_toggle slider ${on ? 'move-right' : 'move-left'}`}></div>
                </div>
            </div>
        );
    };

    const columns: ListColumn<OrderTemplateRow>[] = [
        {
            key: 'templateName',
            label: 'Template Name',
            width: 200,
            minWidth: 120,
            hideable: false,
            render: cells.primary(r => r.templateName),
            mobile: 'title',
            onClick: r => goToTemplate(String(r.id)),
        },
        { key: 'description', label: 'Description', width: 180, minWidth: 100, mobile: 'subtitle' },
        { key: 'validFrom', label: 'Valid From', width: 96, minWidth: 84, value: r => formatDate(r.validFrom), mobile: 'sheet' },
        { key: 'validTo', label: 'Valid To', width: 96, minWidth: 84, value: r => formatDate(r.validTo), mobile: 'sheet' },
        { key: 'location', label: 'Location', width: 140, minWidth: 96, value: r => (r.location ? r.location.name : 'N/A'), mobile: 'sheet' },
        {
            key: 'updatedAt',
            label: 'Last update',
            width: 110,
            minWidth: 92,
            render: cells.date(r => r.updatedAt),
            mobile: 'sheet',
        },
        {
            key: 'status',
            label: 'Status',
            width: 88,
            value: r => (isActiveOf(r) ? 'Active' : 'Inactive'),
            render: r => statusToggle(r),
            mobile: 'trailing',
        },
    ];

    const rowActions: ListRowAction<OrderTemplateRow>[] = [
        {
            key: 'edit',
            label: 'Edit',
            icon: <EditIconV2 />,
            sheetIcon: 'inventory',
            onRun: r => goToTemplate(String(r.id)),
        },
        {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteIconV2 />,
            sheetIcon: 'close',
            danger: true,
            onRun: r => handleShowDelete(String(r.id)),
        },
    ];

    return (
        <>
            <div
                className="h-full relative"
                style={{
                    overflowY: isMobile ? "auto" : undefined,
                    paddingBottom: isMobile ? "24px" : undefined,
                    display: isMobile ? undefined : 'flex',
                    flexDirection: isMobile ? undefined : 'column',
                }}
            >
                <div className="lg:flex items-center justify-between" >
                    <p
                        style={{ color: '#222525' }}
                        className="text-[24px] leading-[130%] font-medium font-inter"
                    >
                        {language && language?.['Order templates']
                            ? language?.['Order templates']
                            : 'Order templates'}
                    </p>
                    <button
                        type="button"
                        className="cursor-pointer flex items-center justify-center"
                        style={{
                            border: 'none',
                            font: 'inherit',
                            gap: '8px',
                            padding: "8px 24px 8px 16px",
                            backgroundColor: '#0F7679',
                            borderRadius: '30px',
                            width: 'fit-content',
                            marginTop: isMobile ? '20px' : '0px',
                        }}
                        onClick={() => {
                            webNavigate('/' + preLanguage + '/orderTemplates/new');
                        }}
                    >
                        <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
                            <PlusIconWhite />
                        </div>
                        <p
                            style={{ color: '#FFFFFF' }}
                            className="text-[14px] leading-[130%] font-medium font-inter"
                        >
                            {language && language?.['Add template']
                                ? language?.['Add template']
                                : 'Add template'}
                        </p>
                    </button>
                </div>
                <ListScreen
                    ref={listRef}
                    language={language}
                    columns={columns}
                    fetchRows={fetchOrderTemplates}
                    searchPlaceholder="Search templates"
                    searchInputRef={searchInputRef}
                    rowActions={rowActions}
                    countNoun="templates"
                    emptyState={inforTable}
                />
                <PopupLocation
                    show={showPopup}
                    title={`${language?.['Get List Order Templates'] ?? 'Get List Order Templates'}`}
                    desc={`${language?.['Please select a location in the bottom left corner to retrieve order templates'] ?? 'Please select a location in the bottom left corner to retrieve order templates'}`}
                    onClose={() => { setShowPopup(false) }}
                    onOpen={() => { setShowPopup(true) }}
                />
            </div>
            <div
                className={`fixed inset-0 flex items-center justify-center px-4 ${showDelete ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-opacity duration-300 ease-in-out`}
                style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100vw", height: "100vh", left: 0, top: 0 }}
            >
                <div
                    className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`} style={{
                        padding: "24px", borderRadius: "24px", backgroundColor: "#FFFFFF", width: "530px", maxWidth: "calc(100vw - 32px)", transition: "transform 0.3s ease, opacity 0.3s ease",
                    }}
                >
                    <div className="flex items-center justify-between">
                        <p
                            style={{ fontSize: '20px', color: '#222525' }}
                            className="font-semibold font-inter leading-[24px]"
                        >
                            {language && language?.['Delete order template?']
                                ? language?.['Delete order template?']
                                : 'Delete order template?'}
                        </p>
                        <div
                            onClick={() => setShowDelete(false)}
                            className="flex items-center justify-center cursor-pointer"
                            style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '100%',
                            }}
                        >
                            <CloseIcon />
                        </div>
                    </div>
                    <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
                        <p className="text-[14px] font-inter leading-[24px]" style={{ color: '#6A7878' }}>
                            {language &&
                                language?.[
                                'This order template will be permanently deleted. Are you sure you want to continue'
                                ]
                                ? language?.[
                                'This order template will be permanently deleted. Are you sure you want to continue'
                                ]
                                : 'This order template will be permanently deleted. Are you sure you want to continue'}
                        </p>
                        {/* <span className="text-main">[sale ID]</span> sale. */}
                    </div>
                    <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
                        <div
                            onClick={() => setShowDelete(false)}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                                borderRadius: '30px',
                                padding: '10px 24px',
                                border: '1px solid #C9D7D8',
                                color: '#222525',
                            }}
                        >
                            {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                        </div>
                        <div
                            onClick={handleDeleteOrderTemplate}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                                borderRadius: '30px',
                                padding: '10px 24px',
                                backgroundColor: '#BA0E13',
                                color: '#FFFFFF',
                            }}
                        >
                            {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
                        </div>
                    </div>
                </div>
            </div>
        </>

    )
};

export default OrderTemplates;